import config from '../config';
import selectedEnvironment from '../env';

const baseURL = config[selectedEnvironment].API_URL;
const CashFlow_API_URL = `${baseURL}/CashFlowForecast`;

const buildQueryString = (params) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        search.append(key, value instanceof Date ? value.toISOString() : value);
    });
    return search.toString();
};

const jsonHeaders = (auth) => ({
    Authorization: 'Bearer ' + auth,
    'Content-Type': 'application/json',
});

/* -------------------------------------------------------------- configuration */

/** Cards configured for the forecast on a case, plus the cash-flow codes they use. */
const GetConfiguration = async (caseId, auth) =>
    fetch(`${CashFlow_API_URL}/Configuration/${encodeURIComponent(caseId)}`, {
        method: 'GET',
        headers: jsonHeaders(auth),
    });

/** Bulk-assigns the cash-flow settings to a set of account cards. */
const UpdateConfiguration = async (payload, auth) =>
    fetch(`${CashFlow_API_URL}/Configuration`, {
        method: 'PUT',
        headers: jsonHeaders(auth),
        body: JSON.stringify(payload),
    });

/** The cash-flow codes in use on a case, for the from/to code selectors. */
const GetCodes = async (caseId, auth) =>
    fetch(`${CashFlow_API_URL}/Codes/${encodeURIComponent(caseId)}`, {
        method: 'GET',
        headers: jsonHeaders(auth),
    });

/* ----------------------------------------------------------- cash-flow settings */

/** Settings of every cash flow on a case plus the bank accounts they can link to. */
const GetSettings = async (caseId, auth) =>
    fetch(`${CashFlow_API_URL}/Settings/${encodeURIComponent(caseId)}`, {
        method: 'GET',
        headers: jsonHeaders(auth),
    });

/** Links one cash flow to a bank account (TBank), or clears the link with a null BankCardId. */
const SaveSetting = async (payload, auth) =>
    fetch(`${CashFlow_API_URL}/Settings`, {
        method: 'PUT',
        headers: jsonHeaders(auth),
        body: JSON.stringify(payload),
    });

/** Removes a cash flow. Fails with 409 while account cards are still assigned to it. */
const DeleteSetting = async (caseId, cashFlowCode, auth) =>
    fetch(
        `${CashFlow_API_URL}/Settings/${encodeURIComponent(caseId)}/${encodeURIComponent(cashFlowCode)}`,
        { method: 'DELETE', headers: jsonHeaders(auth) }
    );

/** Pulls the linked bank account's balance for a day and stores it as that day's opening balance. */
const SyncBankBalance = async (payload, auth) =>
    fetch(`${CashFlow_API_URL}/BankBalance/Sync`, {
        method: 'POST',
        headers: jsonHeaders(auth),
        body: JSON.stringify(payload),
    });

/* --------------------------------------------------------------------- report */

/**
 * The merged actual + expected timeline with its running balance, the summary
 * figures and the chart points. See buildReportRequest in the page for the shape.
 */
const GetReport = async (filters, auth) =>
    fetch(`${CashFlow_API_URL}/Report`, {
        method: 'POST',
        headers: jsonHeaders(auth),
        body: JSON.stringify(filters),
    });

/** The balance carried into a date, used to pre-fill the opening-balance field. */
const GetOpeningBalance = async (payload, auth) =>
    fetch(`${CashFlow_API_URL}/OpeningBalance`, {
        method: 'POST',
        headers: jsonHeaders(auth),
        body: JSON.stringify(payload),
    });

/** The average movement on a card over a recent window, for the card's cash-flow average amount. */
const GetAverageSpend = async (payload, auth) =>
    fetch(`${CashFlow_API_URL}/AverageSpend`, {
        method: 'POST',
        headers: jsonHeaders(auth),
        body: JSON.stringify(payload),
    });

/** Saved bank balance for one calendar day of a cash flow (404 when none exists). */
const GetSavedBankBalance = async (caseId, date, auth, cashFlowCode = null) =>
    fetch(
        `${CashFlow_API_URL}/BankBalance/${encodeURIComponent(caseId)}?${buildQueryString({
            date,
            cashFlowCode,
        })}`,
        { method: 'GET', headers: jsonHeaders(auth) }
    );

/** Every cash flow's stored balance for one day, for the balances dialog. */
const GetDayBalances = async (caseId, date, auth) =>
    fetch(
        `${CashFlow_API_URL}/BankBalances/${encodeURIComponent(caseId)}?${buildQueryString({ date })}`,
        { method: 'GET', headers: jsonHeaders(auth) }
    );

/** Upserts the bank balance for a calendar day on the case. */
const SaveBankBalance = async (payload, auth) =>
    fetch(`${CashFlow_API_URL}/BankBalance`, {
        method: 'PUT',
        headers: jsonHeaders(auth),
        body: JSON.stringify(payload),
    });

/** Removes the saved bank balance for a day so the ledger figure is used again. */
const DeleteBankBalance = async (caseId, date, auth, cashFlowCode = null) =>
    fetch(
        `${CashFlow_API_URL}/BankBalance/${encodeURIComponent(caseId)}?${buildQueryString({
            date,
            cashFlowCode,
        })}`,
        { method: 'DELETE', headers: jsonHeaders(auth) }
    );

/* -------------------------------------------------------- expected transactions */

const GetExpectedTransactions = async (payload, auth) =>
    fetch(`${CashFlow_API_URL}/Expected/Search`, {
        method: 'POST',
        headers: jsonHeaders(auth),
        body: JSON.stringify(payload),
    });

const CreateExpectedTransaction = async (payload, auth) =>
    fetch(`${CashFlow_API_URL}/Expected`, {
        method: 'POST',
        headers: jsonHeaders(auth),
        body: JSON.stringify(payload),
    });

const UpdateExpectedTransaction = async (payload, auth) =>
    fetch(`${CashFlow_API_URL}/Expected`, {
        method: 'PUT',
        headers: jsonHeaders(auth),
        body: JSON.stringify(payload),
    });

const DeleteExpectedTransaction = async (caseId, id, auth) =>
    fetch(`${CashFlow_API_URL}/Expected/${id}?${buildQueryString({ caseId })}`, {
        method: 'DELETE',
        headers: jsonHeaders(auth),
    });

const CancelExpectedTransaction = async (caseId, id, auth) =>
    fetch(`${CashFlow_API_URL}/Expected/${id}/Cancel?${buildQueryString({ caseId })}`, {
        method: 'POST',
        headers: jsonHeaders(auth),
    });

const CompleteExpectedTransaction = async (caseId, id, auth) =>
    fetch(`${CashFlow_API_URL}/Expected/${id}/Complete?${buildQueryString({ caseId })}`, {
        method: 'POST',
        headers: jsonHeaders(auth),
    });

/** Puts a cancelled or completed rule back into the forecast on its own date. */
const ReactivateExpectedTransaction = async (caseId, id, auth) =>
    fetch(`${CashFlow_API_URL}/Expected/${id}/Reactivate?${buildQueryString({ caseId })}`, {
        method: 'POST',
        headers: jsonHeaders(auth),
    });

const PostponeExpectedTransaction = async (caseId, id, newDate, auth) =>
    fetch(`${CashFlow_API_URL}/Expected/${id}/Postpone`, {
        method: 'POST',
        headers: jsonHeaders(auth),
        body: JSON.stringify({ CaseId: caseId, Id: id, NewDate: newDate }),
    });

const DuplicateExpectedTransaction = async (caseId, id, auth) =>
    fetch(`${CashFlow_API_URL}/Expected/${id}/Duplicate?${buildQueryString({ caseId })}`, {
        method: 'POST',
        headers: jsonHeaders(auth),
    });

/** The dates a recurring rule expands to, for the "view occurrences" list. */
const GetOccurrences = async (caseId, id, fromDate, toDate, auth) =>
    fetch(
        `${CashFlow_API_URL}/Expected/${id}/Occurrences?${buildQueryString({
            caseId,
            fromDate,
            toDate,
        })}`,
        { method: 'GET', headers: jsonHeaders(auth) }
    );

/* ------------------------------------------------------------------- matching */

const MatchExpectedToActual = async (payload, auth) =>
    fetch(`${CashFlow_API_URL}/Match`, {
        method: 'POST',
        headers: jsonHeaders(auth),
        body: JSON.stringify(payload),
    });

const GetMatchCandidates = async (caseId, expectedTransactionId, options = {}, auth) =>
    fetch(
        `${CashFlow_API_URL}/MatchCandidates?${buildQueryString({
            caseId,
            expectedTransactionId,
            dayWindow: options.dayWindow,
            amountTolerancePercent: options.amountTolerancePercent,
        })}`,
        { method: 'GET', headers: jsonHeaders(auth) }
    );

/* --------------------------------------------------------------------- export */

/** Returns an xlsx blob response built from the same filters as the report. */
const ExportExcel = async (filters, auth) =>
    fetch(`${CashFlow_API_URL}/Export/Excel`, {
        method: 'POST',
        headers: jsonHeaders(auth),
        body: JSON.stringify(filters),
    });

/** Returns a pdf blob response. Pass html to print exactly what the user sees. */
const ExportPdf = async (caseId, filters, html, auth) =>
    fetch(`${CashFlow_API_URL}/Export/Pdf`, {
        method: 'POST',
        headers: jsonHeaders(auth),
        body: JSON.stringify({ CaseId: caseId, Filters: filters, Html: html || null }),
    });

const CashFlowForecastService = {
    GetConfiguration,
    UpdateConfiguration,
    GetCodes,
    GetSettings,
    SaveSetting,
    DeleteSetting,
    SyncBankBalance,
    GetReport,
    GetOpeningBalance,
    GetAverageSpend,
    GetSavedBankBalance,
    GetDayBalances,
    SaveBankBalance,
    DeleteBankBalance,
    GetExpectedTransactions,
    CreateExpectedTransaction,
    UpdateExpectedTransaction,
    DeleteExpectedTransaction,
    CancelExpectedTransaction,
    CompleteExpectedTransaction,
    ReactivateExpectedTransaction,
    PostponeExpectedTransaction,
    DuplicateExpectedTransaction,
    GetOccurrences,
    MatchExpectedToActual,
    GetMatchCandidates,
    ExportExcel,
    ExportPdf,
};

export default CashFlowForecastService;
